import {
  ChakraFlex,
  CheckboxV2,
  FAIconName,
  FireflyIconButton,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
} from '@equip.health/ui';
import { CareTeamTranslations } from '~/lib/constants/careTeam.constants';
import { PatientEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { CareTeamMemberCardProps } from '~/lib/types/careTeam/careTeam';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

type Props = Pick<
  CareTeamMemberCardProps,
  | 'firstName'
  | 'id'
  | 'lastName'
  | 'onRemove'
  | 'isTemporary'
  | 'onIsTemporaryChange'
  | 'hasTreatmentEpisode'
  | 'handleEndAssignmentChange'
  | 'handleVoidAssignmentChange'
  | 'voidReason'
  | 'patientId'
>;

export const CareTeamMemberCardEditBody = ({
  firstName,
  id,
  lastName,
  onRemove,
  isTemporary,
  onIsTemporaryChange,
  hasTreatmentEpisode,
  handleEndAssignmentChange,
  handleVoidAssignmentChange,
  patientId,
}: Props) => {
  const { trackEvent } = useAnalytics();
  let kebabMenuOptions: FireflyKebabMenuItem[] | undefined;

  if (handleEndAssignmentChange) {
    kebabMenuOptions = [
      {
        label: CareTeamTranslations.END_ASSIGNMENT,
        leftIcon: FAIconName.userEditRegular,
        onClick: () => {
          handleEndAssignmentChange?.(id);
          trackEvent(
            PatientEvents.providerAssignmentEndAssignmentKebabMenuClicked,
            {
              patientId,
              source: AnalyticsSourceValues.CARE_PLAN_TAB,
            },
          );
        },
      },
      {
        label: CareTeamTranslations.VOID_ASSIGNMENT,
        leftIcon: FAIconName.trashRegular,
        leftIconColor: 'critical.base',
        onClick: () => {
          handleVoidAssignmentChange?.(id);
          trackEvent(
            PatientEvents.providerAssignmentVoidAssignmentKebabMenuClicked,
            {
              patientId,
              source: AnalyticsSourceValues.CARE_PLAN_TAB,
            },
          );
        },
        ...(hasTreatmentEpisode
          ? {}
          : {
              disabled: true,
              disabledTooltip:
                CareTeamTranslations.VOID_ASSIGNMENT_DISABLED_TOOLTIP,
            }),
      },
    ];
  }

  return (
    <ChakraFlex alignItems="center">
      {onIsTemporaryChange && (
        <ChakraFlex alignSelf="center" marginRight="36px">
          <CheckboxV2
            id={`care-team-member__user-temporary-checkbox-${id}`}
            isChecked={isTemporary}
            isDisabled={!hasTreatmentEpisode}
            label={CareTeamTranslations.TEMPORARY}
            onChange={() => {
              trackEvent(
                PatientEvents.providerAssignmentTemporaryCheckboxClicked,
                {
                  patientId,
                  source: AnalyticsSourceValues.CARE_PLAN_TAB,
                },
              );
              onIsTemporaryChange(!isTemporary);
            }}
          />
        </ChakraFlex>
      )}
      {onRemove && (
        <FireflyIconButton
          ariaLabel={`Remove ${firstName} ${lastName} from care team`}
          icon={FAIconName.timesRegular}
          id={`care-team-member-card__remove-button-${id}`}
          onClick={onRemove}
          variant="tertiary"
        />
      )}
      {kebabMenuOptions?.length > 0 && (
        <ChakraFlex alignItems="center" margin="10px">
          <FireflyKebabMenu
            aria-label={`Kebab menu for ${firstName} ${lastName}`}
            onMenuOpen={() =>
              trackEvent(
                CarePlanEvents.clickOnKebabMenuInAssignmentEntryInEditProviderTeamModal,
              )
            }
            options={kebabMenuOptions}
          />
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};
